import React, { useCallback, useState, useEffect, useRef } from "react";
import { Calendar as _Calendar, Badge, Text } from "@bookingcom/bui-react";
import useCalendar from "./useCalendar.js";
import { isDateInRange, formatDateKey, getPriceVariant } from "../../utils/fareCalendar.js";
import { useFareCalendar, useFareCalendarEnabled } from "../FareCalendar/useFareCalendar.js";
import useScrollBaseMonthChange from "../../hooks/useScrollBaseMonthChange.js";
import styles from "./Calendar.module.css";
import { t } from "@bookingcom/lingojs-core";
const attributes = {
    key: "data-ui-name",
    value: "calendar_body"
};
export default function Calendar(props) {
    const { segmentIndex, onDateChange, onLoadingChange, onErrorChange, onMonthChange, ...restProps } = props;
    const { setDateValues, isRoundTrip, startDate, endDate, baseDate, fareCalendarConfig, i18n, ...calendarProps } = useCalendar({
        segmentIndex
    });
    const fareCalendarEnabled = useFareCalendarEnabled(segmentIndex, fareCalendarConfig, restProps.mode);
    const [_baseDate] = useState(baseDate);
    const { flightsMap, handleMonthChange, loading, error } = useFareCalendar({
        baseDate: _baseDate,
        mode: restProps.mode,
        enabled: fareCalendarEnabled,
        segmentIndex,
        selectedDepartureDate: startDate || undefined,
        selectedReturnDate: endDate || undefined
    });
    const handleScrollMonthChange = useCallback((date) => {
        handleMonthChange(date);
    }, [handleMonthChange]);
    useScrollBaseMonthChange({
        isActive: restProps.mode === "vertical" && fareCalendarEnabled,
        selector: attributes,
        monthsCallback: handleScrollMonthChange
    });
    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);
    useEffect(() => {
        onErrorChange?.(error);
    }, [error, onErrorChange]);
    const [shouldHideAfterReturn, setShouldHideAfterReturn] = useState(false);
    const [shouldHideBeforeDeparture, setShouldHideBeforeDeparture] = useState(false);
    const previousEndDateRef = useRef(null);
    useEffect(() => {
        if (isRoundTrip && endDate && !previousEndDateRef.current) {
            setShouldHideAfterReturn(true);
        }
        else if (previousEndDateRef.current && !endDate) {
            setShouldHideAfterReturn(false);
        }
        previousEndDateRef.current = endDate;
    }, [endDate, isRoundTrip]);
    useEffect(() => {
        if (isRoundTrip && startDate && !endDate) {
            setShouldHideBeforeDeparture(true);
        }
        else if (endDate || !startDate) {
            setShouldHideBeforeDeparture(false);
        }
    }, [startDate, endDate, isRoundTrip]);
    useEffect(() => {
        setShouldHideAfterReturn(false);
        setShouldHideBeforeDeparture(false);
    }, []);
    const handleOnDateChange = useCallback((data) => {
        const { endDate, startDate } = data;
        let offerAccuracyTrackerId;
        if (startDate && flightsMap) {
            const dateKey = formatDateKey(startDate);
            const flight = flightsMap[dateKey];
            offerAccuracyTrackerId = flight?.pricePerAdult?.offerAccuracyTrackerId ?? "";
        }
        setDateValues({ from: startDate || undefined, to: endDate || undefined });
        onDateChange({
            from: startDate || undefined,
            to: endDate || undefined,
            offerAccuracyTrackerId
        });
    }, [setDateValues, onDateChange, flightsMap]);
    const renderDay = useCallback((date) => {
        const yyyy = date.getFullYear();
        const m = date.getMonth() + 1;
        const d = date.getDate();
        const mm = `0${m}`.slice(-2);
        const dd = `0${d}`.slice(-2);
        return React.createElement("span", { "data-ui-name": `calendar_cell_${yyyy}-${mm}-${dd}` }, d);
    }, []);
    const renderAttachment = useCallback(({ date }) => {
        if (!isDateInRange(date, calendarProps.minDate, calendarProps.maxDate)) {
            return undefined;
        }
        if (isRoundTrip) {
            if (shouldHideBeforeDeparture && startDate && date < startDate) {
                return undefined;
            }
            if (shouldHideAfterReturn && endDate && date > endDate) {
                return undefined;
            }
        }
        if (loading) {
            return {
                loading: true
            };
        }
        const dateKey = formatDateKey(date);
        const flight = flightsMap[dateKey];
        if (!flight || !flight.formattedPrice) {
            return {
                text: "-"
            };
        }
        const variant = getPriceVariant(flight.priceIndication);
        return {
            text: flight.formattedPrice,
            variant
        };
    }, [
        calendarProps.minDate,
        calendarProps.maxDate,
        flightsMap,
        loading,
        isRoundTrip,
        startDate,
        endDate,
        shouldHideAfterReturn,
        shouldHideBeforeDeparture
    ]);
    const onBaseMonthChange = useCallback((date) => {
        handleMonthChange(date);
        onMonthChange?.(date);
    }, [handleMonthChange, onMonthChange]);
    const currencyCode = fareCalendarConfig?.currencyCode;
    const calendarClassName = restProps.mode === "vertical" ? styles.verticalMode : undefined;
    return (React.createElement(React.Fragment, null,
        fareCalendarEnabled && currencyCode ? (React.createElement(Badge, { text: `Showing latest prices in ${currencyCode}`, alternative: true, className: styles.fareCalendarBadge })) : undefined,
        React.createElement(_Calendar, { ...calendarProps, ...restProps, onDateChange: handleOnDateChange, startDate: startDate, endDate: isRoundTrip ? endDate : null, baseDate: _baseDate, renderDay: renderDay, attributes: { [attributes.key]: attributes.value }, renderAttachment: fareCalendarEnabled ? renderAttachment : undefined, onBaseMonthChange: onBaseMonthChange, className: calendarClassName, previousAriaLabel: i18n.trans(t("a11y_flights_calendar_previous")), nextAriaLabel: i18n.trans(t("a11y_flights_calendar_next")) }),
        fareCalendarEnabled ? (React.createElement(Text, { variant: "body_2", className: styles.fareCalendarFooter }, "Prices are per adult and based on the last 30 days")) : undefined));
}
